import { inject, LOCALE_ID } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { SAMPLE_POLL_LANGUAGES, SamplePollLanguage } from 'whocan-lib';
import { SamplePollSlugService } from '../../core/sample-poll-slug.service';
import { RouteContextService } from '../../../shared/services/route-context.service';

/**
 * Resolver fuer /{lang}/poll-templates/:slug (und lokalisierte Varianten).
 *
 * Aufloesungs-Strategie:
 * 1. Versuche `samplePollSlugs/{slug}/{requestedLang}` — User-Sprache aus `LOCALE_ID`
 * 2. Falls leer: versuche `samplePollSlugs/{slug}/en` (Fallback)
 *    - `isFallback=true` markiert das, damit Page Banner + Canonical setzen kann
 * 3. Falls auch EN leer: echtes 404
 *
 * Wichtige UX-Entscheidung: KEIN Redirect bei Fallback. User bleibt auf seiner
 * Sprach-URL, sieht aber EN-Content. Die App-Chrome bleibt in der User-Sprache;
 * nur Title, Description und Optionen des Polls sind EN.
 *
 * SEO-Plumbing (passiert in der SamplePollVotePage anhand der Resolver-Daten):
 * - Bei Fallback: `<link rel="canonical">` auf die EN-URL setzen, damit Google die
 *   EN-URL als kanonische Variante indexiert und keine Duplicate-Content-Strafe gibt
 * - Hreflang-Tags fuer alle existierenden Sprach-Varianten dieses Slugs setzen
 *   (siehe `slugService.getAllLanguageVariants`)
 */
export interface SamplePollResolvedKeys {
    ownerId: string;
    pollId: string;
    slug: string;
    /** Tatsaechlich aufgeloeste Sprache (kann bei Fallback != requestedLang sein). */
    resolvedLang: SamplePollLanguage;
    /** Sprache aus der URL des Users (LOCALE_ID-basiert). */
    requestedLang: SamplePollLanguage;
    /** true wenn requestedLang != resolvedLang (also EN-Content unter Nicht-EN-URL). */
    isFallback: boolean;
}

export const samplePollResolver: ResolveFn<SamplePollResolvedKeys> = async (route: ActivatedRouteSnapshot) => {
    const logger = inject(LoggerService);
    const slugService = inject(SamplePollSlugService);
    const router = inject(Router);
    const locale = inject(LOCALE_ID) as string;
    const routeContext = inject(RouteContextService);

    const slug = route.params['slug'];
    logger.log('samplePollResolver: slug=', slug, 'locale=', locale);

    const requestedLang = normalizeLocale(locale);

    // Canonical + hreflang SOFORT und rein URL-basiert setzen — VOR dem RTDB-Read unten.
    // Firebases robots.txt blockt Googlebot fuer die RTDB-Long-Polling-Requests
    // (`*.firebaseio.com/.lp`), wodurch `getPollKeysBySlug` fuer Googlebot haengt: der Resolver
    // liefert nie Keys, die Route aktiviert nie, NavigationEnd feuert nie. Alles Resolver-/
    // Header-/Page-basierte (Title, Canonical via setupSamplePollAlternates) bliebe damit fuer
    // Google unsichtbar. Slug + URL-Sprache reichen aber fuer einen korrekten Self-Canonical +
    // vollstaendiges hreflang-Set — und dieser synchrone Aufruf laeuft, bevor der Read blockt.
    // Der async-Pfad unten (setupSamplePollAlternates in der Page) verfeinert das nur noch fuer
    // echte User/Dev (hreflang auf existierende Varianten, bei Fallback Canonical auf EN).
    if (slug) {
        routeContext.setSamplePollAlternatesFromUrl(slug, requestedLang);
    }

    // 1. User-Sprache versuchen
    const primary = await slugService.getPollKeysBySlug(slug, requestedLang);
    if (primary) {
        return {
            ownerId: primary.ownerId,
            pollId: primary.pollId,
            slug,
            resolvedLang: requestedLang,
            requestedLang,
            isFallback: false,
        };
    }

    // 2. EN-Fallback (sofern requestedLang != 'en')
    if (requestedLang !== 'en') {
        const fallback = await slugService.getPollKeysBySlug(slug, 'en');
        if (fallback) {
            logger.log('samplePollResolver: using EN fallback for', slug, 'requested', requestedLang);
            return {
                ownerId: fallback.ownerId,
                pollId: fallback.pollId,
                slug,
                resolvedLang: 'en',
                requestedLang,
                isFallback: true,
            };
        }
    }

    // 3. Keine Variante existiert -> 404
    logger.warn('samplePollResolver: no entry for slug', slug, 'in any language');
    router.navigateByUrl('/404', { skipLocationChange: true });
    return null;
};

function normalizeLocale(locale: string): SamplePollLanguage {
    const short = (locale || '').substring(0, 2).toLowerCase() as SamplePollLanguage;
    return SAMPLE_POLL_LANGUAGES.includes(short) ? short : 'en';
}
